import { UserAppEvents } from '@duke-hq/ui/components/UserAppEvents/UserAppEvents';
import { RouterOutputs } from '@duke-hq/svc-api/provider';
import {
  Avatar,
  Box,
  Center,
  Flex,
  Grid,
  GridItem,
  Link as ChakraLink,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Txt, TxtMuted } from '@duke-hq/app-provider-web/components/util/Txt';
import { Link as ReactRouterLink } from 'react-router-dom';
import { skipToken } from '@tanstack/react-query';

export function TeamInternalEnrolmentsList({
  member,
}: {
  member: RouterOutputs['users']['consultantById'] | undefined;
}) {
  const { data: userEnrolments, isLoading: isLoadingEnrolments } =
    trpcTenant.enrolments.getByUserEmail.useQuery(
      member?.email != null ? { email: member?.email } : skipToken,
      { enabled: Boolean(member?.email) },
    );

  const studentId = userEnrolments?.[0]?.user_id;
  const consultantId = userEnrolments?.[0]?.user?.studentData?.owned_by;

  const { data: consultant } = trpcTenant.users.consultantById.useQuery(
    consultantId != null ? { id: consultantId } : skipToken,
    { enabled: Boolean(consultantId) },
  );

  const { data: recentActivity } = trpcTenant.users.studentActivity.useQuery(
    studentId != null ? { userId: studentId } : skipToken,
    { enabled: Boolean(studentId) },
  );

  if (!isLoadingEnrolments && (!userEnrolments || userEnrolments.length === 0)) {
    return (
      <Center>
        <VStack spacing="2" alignItems="center" marginY={4}>
          <Trans>No enrolments available</Trans>
        </VStack>
      </Center>
    );
  }

  return (
    <Grid w="full" templateColumns="repeat(4, 1fr)" h="full" alignItems="stretch">
      <GridItem colSpan={2}>
        {isLoadingEnrolments ? (
          <Center p={4}>
            <Spinner />
          </Center>
        ) : (
          <StudentEnrolments enrolments={userEnrolments ?? []} />
        )}
      </GridItem>

      <GridItem colSpan={2} h="full">
        <Flex
          h="full"
          bg="bg.canvas"
          borderLeftWidth="1px"
          direction="column"
          alignItems="flex-start"
          gap="8px"
          width="full"
        >
          <Box
            width="100%"
            bg="bg.surface"
            paddingLeft="8px"
            borderBottomWidth="2px"
            borderBottomColor="gray.100"
            color="fg.subtle"
            p={4}
          >
            <Grid templateRows="repeat(2, 1fr)" templateColumns="auto 1fr" rowGap={3}>
              <GridItem>
                <Text p={2} textStyle="sm" color="fg.muted">
                  Assessor:
                </Text>
              </GridItem>
              <GridItem>
                <Flex alignItems="center" py={1.5} px={2}>
                  <Text fontSize="sm" color="fg.default" fontWeight="medium">
                    N/A
                  </Text>
                </Flex>
              </GridItem>
              <GridItem>
                <Text p={2} textStyle="sm" color="fg.muted">
                  Consultant:
                </Text>
              </GridItem>
              <GridItem>
                <Flex alignItems="center" py={1.5} px={2}>
                  {member ? <AvatarName user={consultant} /> : '-'}
                </Flex>
              </GridItem>
            </Grid>
          </Box>

          {recentActivity ? (
            <Box py={{ base: '4', md: '8' }} px={4} flex="1" width="full">
              <UserAppEvents
                studentActivityItems={recentActivity.items.map((activity) => ({
                  msg: activity.msg ?? '',
                  pk: activity.pk ?? '',
                  sk: activity.sk ?? '',
                  data: activity.data ?? {},
                }))}
              />
            </Box>
          ) : null}
        </Flex>
      </GridItem>
    </Grid>
  );
}

function StudentEnrolments({
  enrolments,
}: {
  enrolments: RouterOutputs['enrolments']['getByUserEmail'];
}) {
  if (enrolments?.length > 0) {
    return (
      <Flex direction="column" w="full" h="full">
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>
                  <Trans>Enrolments</Trans>
                </Th>
                <Th>
                  <Trans>Course</Trans>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {enrolments?.map((enrolment) => {
                return (
                  <Tr key={enrolment.id}>
                    <Td width={'15%'}>
                      <ChakraLink as={ReactRouterLink} to={`/team/enrolment/${enrolment.id}`}>
                        {enrolment.id}
                      </ChakraLink>
                    </Td>
                    <Td>
                      <Txt fontWeight="extrabold">{enrolment.course?.title}</Txt>
                      <TxtMuted>{enrolment.course?.identifier ?? '-'}</TxtMuted>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Flex>
    );
  }

  return null;
}

function AvatarName({ user }: { user: RouterOutputs['users']['consultantById'] | undefined }) {
  if (user) {
    const consultantFullName =
      user.first_name + (typeof user.last_name === 'string' ? ` ${user.last_name}` : '');
    return (
      <Flex gap={3} alignItems="center">
        <Avatar size="xs" name={consultantFullName} src={user.picture_url ?? undefined} />
        <Text fontSize="sm" color="fg.default" fontWeight="medium">
          {consultantFullName}
        </Text>
      </Flex>
    );
  }
}
